import { z } from 'zod';
import { EXIT_KEYS } from '../enums';
import { decimalInRange, decimalStringSchema, jsonObjectSchema } from './common';
import { symbolSchema } from './universe';

/**
 * Backtests (API_DOCS.md — Backtests): replay history through a strategy and report what it
 * would have earned, net of fees and slippage, next to simply holding BTC.
 *
 *   technical_hunter — the live Strategy C code on 5m (or 1m) bars, exits by one of the live
 *                      exit strategies. Short ranges only: intraday bars are heavy.
 *   trend_follower   — daily Donchian-breakout ensemble with volatility sizing (Zarattini,
 *                      Pagani & Barbon 2025, "Catching Crypto Trends"). Years of history.
 */

export const BACKTEST_STRATEGIES = ['technical_hunter', 'trend_follower'] as const;
export type BacktestStrategy = (typeof BACKTEST_STRATEGIES)[number];

export const BACKTEST_STATUSES = ['queued', 'running', 'done', 'failed'] as const;
export type BacktestStatus = (typeof BACKTEST_STATUSES)[number];

export const trendFollowerParamsSchema = z.object({
  /** Donchian lookbacks in days; each is one vote in the ensemble. */
  lookbacks: z.array(z.number().int().min(5).max(400)).min(1).max(10).default([10, 20, 30, 60, 90, 150, 250, 360]),
  /** Largest share of equity one coin may take, in %. */
  maxCoinPct: decimalStringSchema.refine(decimalInRange('1', '100'), 'must be between 1 and 100').default('20'),
  /** Annualised volatility a full-size position aims for; more volatile coins get less, in %. */
  targetVolPct: decimalStringSchema.refine(decimalInRange('5', '300'), 'must be between 5 and 300').default('60'),
  /** Window for the volatility estimate, in days. */
  volLookbackDays: z.number().int().min(10).max(365).default(90),
  /** No new buys while BTC closes below its 200-day average. */
  btcFilter: z.boolean().default(true),
  /** Skip rebalances smaller than this share of the position, in % — saves fees. */
  rebalanceThresholdPct: decimalStringSchema.refine(decimalInRange('0', '100'), 'must be between 0 and 100').default('25'),
  /**
   * Point-in-time universe: each day only the `topN` coins with the highest 30-day median
   * daily volume AS OF THAT DAY may be held, and only above this floor (USDT). Choosing coins
   * by today's volume would pick yesterday's winners in hindsight.
   */
  minMedianVolumeUsdt: decimalStringSchema.refine(decimalInRange('0', '100000000000'), 'must be ≥ 0').default('10000000'),
  /**
   * 'continuous' — the paper's version: the position follows the signal every day, scaling
   * in and out. 'binary' — what the live bot runs: buy the full size once the signal reaches
   * `entrySignal`, hold without resizing, sell everything below `exitSignal`.
   */
  mode: z.enum(['continuous', 'binary']).default('binary'),
  entrySignal: decimalStringSchema.refine(decimalInRange('0.01', '1'), 'must be between 0.01 and 1').default('0.5'),
  exitSignal: decimalStringSchema.refine(decimalInRange('0', '1'), 'must be between 0 and 1').default('0.25'),
  /** binary: at most this many coins at once (live: the strategy's own slot limit). */
  maxPositions: z.number().int().min(1).max(50).default(50),
  /** Share of equity this strategy may have invested, in % (live: its capital pool). */
  capitalPct: decimalStringSchema.refine(decimalInRange('1', '100'), 'must be between 1 and 100').default('100'),
  /**
   * binary, when every slot is taken: sell the weakest held coin (signal below
   * `rotateBelowSignal`, held ≥ `minHoldDays`) to buy a candidate at `rotateMinSignal`+.
   */
  rotation: z.boolean().default(false),
  rotateMinSignal: decimalStringSchema.refine(decimalInRange('0.01', '1'), 'must be between 0.01 and 1').default('1'),
  rotateBelowSignal: decimalStringSchema.refine(decimalInRange('0', '1'), 'must be between 0 and 1').default('0.5'),
  minHoldDays: z.number().int().min(0).max(60).default(3),
});
export type TrendFollowerParams = z.infer<typeof trendFollowerParamsSchema>;

export const backtestRequestSchema = z
  .object({
    strategy: z.enum(BACKTEST_STRATEGIES),
    from: z.string().date(),
    to: z.string().date(),
    /** Explicit coins, or omit and give `topN` to take today's most traded USDT pairs. */
    symbols: z.array(symbolSchema).min(1).max(50).optional(),
    topN: z.number().int().min(1).max(50).default(20),
    initialCapital: decimalStringSchema.refine(decimalInRange('100', '100000000'), 'must be between 100 and 100000000').default('10000'),
    feePct: decimalStringSchema.refine(decimalInRange('0', '1'), 'must be between 0 and 1').default('0.1'),
    slippageBps: z.number().int().min(0).max(100).default(5),
    /**
     * Intraday strategies: bid–ask spread paid on top of slippage, in bps of price — half on
     * the buy, half on the sell. History has no order book, so this is a flat estimate.
     */
    spreadBps: z.number().int().min(0).max(200).default(10),
    /** Strategy params; validated against the strategy's own schema. Defaults = live config. */
    params: jsonObjectSchema.optional(),
    /** Intraday strategies only; default = the strategy's own exit, else the exit in use. */
    exit: z.object({ exitKey: z.enum(EXIT_KEYS), params: jsonObjectSchema }).optional(),
    /** Intraday strategies only: share of equity per trade, in %, and open-trade cap. */
    positionPct: decimalStringSchema.refine(decimalInRange('1', '100'), 'must be between 1 and 100').default('10'),
    maxConcurrent: z.number().int().min(1).max(50).default(3),
  })
  .refine((r) => r.from < r.to, { message: '"from" must be before "to"', path: ['to'] });
export type BacktestRequest = z.infer<typeof backtestRequestSchema>;

export interface BacktestMetrics {
  readonly startEquity: string;
  readonly endEquity: string;
  readonly totalReturnPct: string;
  /** Annualised; null for ranges under 30 days, where it would be noise. */
  readonly cagrPct: string | null;
  readonly maxDrawdownPct: string;
  /** Daily-return Sharpe, annualised with √365; null with too few days. */
  readonly sharpe: string | null;
  readonly trades: number;
  readonly wins: number;
  readonly winRatePct: string | null;
  readonly profitFactor: string | null;
  readonly feesPaid: string;
  /** Average share of equity invested, in %. */
  readonly avgExposurePct: string;
  readonly avgWin: string | null;
  readonly avgLoss: string | null;
  readonly longestLosingStreak: number;
}

/** Return over one slice of the range — an edge that lives in one regime only is a bet. */
export interface BacktestPeriod {
  readonly label: string;
  readonly returnPct: string;
  readonly maxDrawdownPct: string;
  readonly btcHoldReturnPct: string | null;
}

export interface BacktestBaseline {
  /** 'btc_hold' | 'equal_weight_hold' | 'random_entries' */
  readonly key: string;
  readonly returnPct: string | null;
}

export interface BacktestTrade {
  readonly symbol: string;
  readonly entryAt: string;
  readonly exitAt: string;
  readonly entryPrice: string;
  readonly exitPrice: string;
  /** Money that went in (cost of the buys, fees included). */
  readonly invested: string;
  readonly pnl: string;
  readonly pnlPct: string;
  readonly fees: string;
  readonly reason: string;
  /** Intraday: best / worst price while open vs the entry, in % (absent in older runs). */
  readonly mfePct?: string | null;
  readonly maePct?: string | null;
}

export interface BacktestSymbolResult {
  readonly symbol: string;
  readonly net: string;
  readonly profit: string;
  readonly loss: string;
  readonly fees: string;
  readonly trades: number;
  readonly wins: number;
  readonly losses: number;
}

export interface BacktestPoint {
  /** Epoch ms (UTC). */
  readonly t: number;
  readonly equity: string;
  /** The same capital held in BTC from the first point on — the benchmark to beat. */
  readonly btcHold: string | null;
}

export const BACKTEST_VERDICTS = ['trusted', 'suspect', 'invalid'] as const;
export type BacktestVerdict = (typeof BACKTEST_VERDICTS)[number];

export interface BacktestResult {
  readonly metrics: BacktestMetrics;
  readonly benchmark: { readonly btcHoldReturnPct: string | null; readonly btcHoldMaxDrawdownPct: string | null };
  /** Do-nothing and random-entry baselines the strategy has to beat. */
  readonly baselines: BacktestBaseline[];
  /** Same signals with fees and slippage × 1.5. */
  readonly stressedReturnPct: string | null;
  readonly verdict: BacktestVerdict;
  /** Codes: too_few_trades, few_trades, sharpe_too_high, win_rate_too_high, below_baseline (value = baseline key), not_profitable, fails_cost_stress. */
  readonly verdictReasons: { readonly code: string; readonly value?: string }[];
  /** Backtests run so far for this strategy, this one included — the multiple-testing count. */
  readonly trials: number;
  /** Per calendar year plus the last 90 days. */
  readonly periods: BacktestPeriod[];
  /** Strategy params actually used (defaults filled in). */
  readonly params: Record<string, unknown>;
  /** Git revision of the code that produced this run. */
  readonly revision: string;
  readonly equity: BacktestPoint[];
  readonly bySymbol: BacktestSymbolResult[];
  /** Newest first, at most 1000. */
  readonly trades: BacktestTrade[];
  readonly symbols: string[];
  /** Caveat codes shown next to the numbers: survivorship, gates_not_simulated, btc_regime_approx_<tf>, no_data:<symbol>. */
  readonly warnings: string[];
}

export interface BacktestSummary {
  readonly id: string;
  readonly strategy: BacktestStrategy;
  readonly status: BacktestStatus;
  /** 0–100 while running. */
  readonly progress: number;
  readonly request: BacktestRequest;
  readonly createdAt: string;
  readonly finishedAt: string | null;
  readonly error: string | null;
  readonly headline: { readonly totalReturnPct: string; readonly btcHoldReturnPct: string | null; readonly maxDrawdownPct: string; readonly trades: number; readonly verdict: BacktestVerdict | null } | null;
}

export interface BacktestDetail extends BacktestSummary {
  readonly result: BacktestResult | null;
}
